import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_MARKET_INSTANT_BUY } from '@sorare/core/src/constants/routes';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { isType } from '@sorare/core/src/gql';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { useProbabilisticBundleConfigSummary } from '@sorare/core/src/hooks/useProbabilisticBundleConfigSummary';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { NeonChristmasBackground } from '../NeonChristmasBackground';
import { RewardName } from '../RewardName';
import player1Url from '../assets/player1.svg';
import player2Url from '../assets/player2.svg';
import { BoldWhite } from './BoldWhite';
import { Eligibility } from './Eligibility';
import { Rewards } from './Rewards';
import boxUrl from './Rewards/assets/box.png';
import cardsUrl from './Rewards/assets/cards.png';
import {
  Explanation_PromotionalEventInterface,
  Explanation_PromotionalEventInterface_AirdropPromotionalEvent_,
  Explanation_PromotionalEventInterface_MarketPromotionalEvent_,
  Explanation_PromotionalEventInterface_SpecialEditionPromotionalEvent_,
  claimAirdropMutation,
  claimAirdropMutationVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  padding: var(--quadruple-unit) var(--double-unit);
  background-repeat: no-repeat;
  margin: auto;
  height: 100%;
`;

type Props = {
  event: Explanation_PromotionalEventInterface;
};

export const MarketExplanation = ({
  event,
}: Props & {
  event:
    | Explanation_PromotionalEventInterface_MarketPromotionalEvent_
    | Explanation_PromotionalEventInterface_SpecialEditionPromotionalEvent_;
}) => {
  const { sport, slug } = event;
  const { formatMessage } = useIntl();
  const summary = useProbabilisticBundleConfigSummary(
    event.rewardsProbabilisticBundleConfig[0]
  );
  return (
    <Wrapper
      style={
        summary[formatMessage(glossary.cards)]
          ? {
              backgroundImage: `linear-gradient(to top, black 10%, transparent), url(${player1Url}), url(${player2Url})`,
              backgroundSize: `100% 40%, 80% auto, 60% auto`,
              backgroundPosition: `left bottom, -200% 100%, 150% 100%`,
            }
          : {}
      }
    >
      <Chip>
        <FontAwesomeIcon icon={faClock} />
        <TimeLeft time={event.endDate} />
      </Chip>
      <Rewards event={event} />

      <Eligibility
        eligibilityRules={event.eligibilityRules}
        sport={event.sport}
      />
      <Button
        fullWidth
        size="medium"
        to={`${generateSportPath(ANY_SPORT_MARKET_INSTANT_BUY, {
          sport,
        })}?${SEARCH_PARAMS.PROMOTIONAL_EVENT}=${slug}#market`}
        color="primary"
      >
        <FormattedMessage
          id="NeonChristmas.explanation.cta"
          defaultMessage="Browse offers"
        />
      </Button>
      {event.termsAndConditionsUrl && (
        <a href={event.termsAndConditionsUrl}>T&C</a>
      )}
    </Wrapper>
  );
};

const CLAIM_MUTATION = gql`
  mutation claimAirdropMutation($id: ID!, $sport: Sport) {
    claimPromotionalEventAirdrop(input: { promotionalEventId: $id }) {
      currentUser {
        slug
        unopenedProbabilisticBundles(sport: $sport) {
          totalCount
        }
      }
      promotionalEvent {
        slug
        claimed
      }
    }
  }
` as TypedDocumentNode<claimAirdropMutation, claimAirdropMutationVariables>;

export const AirdropExplanation = ({
  event,
}: Props & {
  event: Explanation_PromotionalEventInterface_AirdropPromotionalEvent_;
}) => {
  const { showNotification } = useSnackNotificationContext();

  const [claim, { loading }] = useMutation(CLAIM_MUTATION, {
    variables: { id: event.id, sport: event.sport },
  });
  const [forceClaimed, setClaimed] = useState(event.claimed);
  const isClaimed = event.claimed || forceClaimed;
  return (
    <Wrapper>
      <Chip>
        <FontAwesomeIcon icon={faClock} />
        <TimeLeft time={event.endDate} />
      </Chip>

      <BodyM color="var(--c-nd-600)">
        <FormattedMessage
          id="NeonChristmas.explanation.airdrop"
          defaultMessage="Claim your"
        />
      </BodyM>
      <RewardName>
        <FormattedMessage
          id="NeonChristmas.explanation.freebox"
          defaultMessage="Free Box"
        />
      </RewardName>
      <img src={boxUrl} alt="" />

      {isClaimed ? (
        <Button
          fullWidth
          size="medium"
          color="primary"
          to={generateSportPath(ANY_SPORT_PLAY_INVENTORY_BOXES_TYPE, {
            sport: event.sport,
            params: { type: 'limited' },
          })}
        >
          <FormattedMessage
            id="NeonChristmas.explanation.airdrop.inventory"
            defaultMessage="Go to the inventory"
          />
        </Button>
      ) : (
        <LoadingButton
          fullWidth
          size="medium"
          color="primary"
          loading={loading}
          disabled={event.claimed}
          onClick={async () => {
            if (!event.claimed) {
              await claim();
              setClaimed(true);
              showNotification('neonChristmasReward');
            }
          }}
        >
          <FormattedMessage
            id="NeonChristmas.explanation.airdrop.cta"
            defaultMessage="Claim"
          />
        </LoadingButton>
      )}
      {event.termsAndConditionsUrl && (
        <a href={event.termsAndConditionsUrl}>T&C</a>
      )}
    </Wrapper>
  );
};

export const SpecialEditionExplanation = ({
  event,
}: Props & {
  event: Explanation_PromotionalEventInterface_SpecialEditionPromotionalEvent_;
}) => {
  const { sport } = event;
  return (
    <Wrapper>
      <Chip>
        <FontAwesomeIcon icon={faClock} />
        <TimeLeft time={event.endDate} />
      </Chip>
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage
          id="NeonChristmas.explanation.reward.SpecialEdition.percentage"
          defaultMessage="<b>{probability, number, percent}</b> chance the card you buy is a"
          values={{
            probability: (event.revealableCardConfig ?? [])[0]
              ?.revealedEditionsConfig[0]?.odds,
            b: BoldWhite,
          }}
        />
      </BodyM>
      <RewardName>
        <FormattedMessage
          id="NeonChristmas.explanation.reward.specialEdition"
          defaultMessage="Special edition"
        />
      </RewardName>
      <img src={cardsUrl} alt="" />
      <Eligibility eligibilityRules={event.eligibilityRules} />
      <Button
        fullWidth
        size="medium"
        to={generateSportPath(ANY_SPORT_MARKET_INSTANT_BUY, {
          sport,
        })}
        color="primary"
      >
        <FormattedMessage
          id="NeonChristmas.explanation.cta"
          defaultMessage="Browse offers"
        />
      </Button>
      {event.termsAndConditionsUrl && (
        <a href={event.termsAndConditionsUrl}>T&C</a>
      )}
    </Wrapper>
  );
};

export const Explanation = ({ event }: Props) => {
  const content = () => {
    if (isType(event, 'AirdropPromotionalEvent')) {
      return <AirdropExplanation event={event} />;
    }
    if (isType(event, 'MarketPromotionalEvent')) {
      return <MarketExplanation event={event} />;
    }
    if (isType(event, 'SpecialEditionPromotionalEvent')) {
      return <SpecialEditionExplanation event={event} />;
    }
    return null;
  };
  return <NeonChristmasBackground>{content()}</NeonChristmasBackground>;
};

Explanation.fragments = {
  PromotionalEventInterface: gql`
    fragment Explanation_PromotionalEventInterface on PromotionalEventInterface {
      slug

      name
      startDate
      endDate
      termsAndConditionsUrl
      eligibilityRules {
        id
        ...Eligibility_EligibilityRules
      }
      ... on AirdropPromotionalEvent {
        slug
        sport
        claimed
      }
      ... on MarketPromotionalEvent {
        slug
        sport
        rewardsProbabilisticBundleConfig {
          id
          ...useProbabilisticBundleConfigSummary_ProbabilisticBundleConfig
        }
      }
      ... on SpecialEditionPromotionalEvent {
        slug
        revealableCardConfig {
          revealedEditionsConfig {
            odds
          }
        }
        sport
      }
      ...Rewards_PromotionalEventInterface
    }
    ${Rewards.fragments.PromotionalEventInterface}
    ${Eligibility.fragments.EligibilityRules}
    ${useProbabilisticBundleConfigSummary.fragments.ProbabilisticBundleConfig}
  ` as TypedDocumentNode<Explanation_PromotionalEventInterface>,
};
